import {Component, computed, effect, inject, input, PLATFORM_ID, signal, Signal, WritableSignal} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Image, Maybe, WineProduct} from '@core/interfaces/generated/graphql';

import {ProductCarouselComponent} from './product-image-carousel/product-image-carousel.component';
import {
  ProductImageThumbnailCarouselComponent
} from './product-image-thumbnail-carousel/product-image-thumbnail-carousel.component';
import {TrackEventService} from '@core/services/track-event.service';
import {captureException} from '@sentry/angular';
import {isPlatformBrowser} from '@angular/common';

export type ProductImage = {
  id: string,
  original: string,
  large: string,
  small: string,
  scriptId?: string,
  is360: boolean,
  vismaId?: string,
  isGiftbox?: boolean,
  roles?: Array<Maybe<string>>,
  originalIndex?: number,
}

@Component({
  selector: 'app-product-images',
  templateUrl: './product-images.component.html',
  styleUrls: ['./product-images.component.scss'],
  imports: [ProductCarouselComponent, ProductCarouselComponent, ProductImageThumbnailCarouselComponent],
  'host': {ngSkipHydration: 'true'},
})

export class ProductImagesComponent {
  private modalService = inject(NgbModal);
  private trackEventService = inject(TrackEventService);
  public orbitvuError = signal(false);

  public carouselImageArray: WritableSignal<ProductImage[] | null> = signal(null);
  private platformId = inject(PLATFORM_ID);
  private modalReference: NgbModalRef;
  product = input<WineProduct | undefined>();
  images = computed<ProductImage[] | undefined>(() => {
    const images = this.product()?.images;
    const imagesFormatted = images?.map((image, originalIndex) => {
      const isGiftbox = image?.roles?.includes('giftbox_image') ?? false;
      return {
        id: image?.id ?? '',
        original: image?.original ?? '',
        large: image?.large ?? '',
        small: image?.small ?? '',
        scriptId: '',
        is360: false,
        isGiftbox,
        roles: image?.roles ?? [],
        originalIndex,
      }
    });

    // Sort images: a single giftbox image (if any) is moved to the front, preserving original order otherwise
    const sortedImages = this.sortImagesByGiftboxPriority(imagesFormatted);

    if (this.product()?.has360BottleImage && !this.orbitvuError() && isPlatformBrowser(this.platformId) && sortedImages && sortedImages.length > 0) {
      // Find the image that should have the 360 viewer based on role priority
      const image360Index = this.find360ImageIndex(sortedImages);
      
      if (image360Index !== -1) {
        return sortedImages.map((image, index) => {
          if (index === image360Index) {
            return {
              ...image,
              id: this.product()?.vismaProductNumber ?? '',
              vismaId: this.product()?.vismaProductNumber,
              is360: true,
            };
          }
          return image;
        });
      }
    }
    return sortedImages;
  });

  /**
   * Moves the (unique) giftbox-tagged image to the first position while keeping the rest in original order.
   */
  private sortImagesByGiftboxPriority(images: ProductImage[] | undefined): ProductImage[] | undefined {
    if (!images || images.length <= 1) {
      return images;
    }

    const giftboxIndex = images.findIndex(img => img.isGiftbox);
    if (giftboxIndex <= 0) {
      return images;
    }

    const giftboxImage = images[giftboxIndex];
    return [giftboxImage, ...images.slice(0, giftboxIndex), ...images.slice(giftboxIndex + 1)];
  }

  /**
   * Finds the image that should display the 360 viewer based on role priority.
   * Priority: front_image > base > thumbnail > small_image > first non-giftbox image
   * Note: Giftbox images are excluded from 360 viewer consideration.
   */
  private find360ImageIndex(images: ProductImage[]): number {
    const rolePriority = ['front_image', 'base', 'thumbnail', 'small_image'];
    
    // Try to find image with priority roles (excluding giftbox images)
    for (const targetRole of rolePriority) {
      const imageIndex = images.findIndex(img => !img.isGiftbox && img.roles?.includes(targetRole));
      if (imageIndex !== -1) {
        return imageIndex;
      }
    }
    
    // Fallback: first non-giftbox image
    const firstNonGiftboxIndex = images.findIndex(img => !img.isGiftbox);
    return firstNonGiftboxIndex !== -1 ? firstNonGiftboxIndex : 0;
  }

  constructor() {
    effect(() => {
      const images = this.images();
      if (images && images[0]?.id) {
        const carouselImageArray = this.carouselImageArray();
        const diff = images.filter(image => !carouselImageArray?.find(carouselImage => carouselImage.id === image.id));
        if (carouselImageArray === null || diff.length > 0) {
          this.carouselImageArray.set(images);
        }
      }
    });
  }

  public _activeImageId = signal<string | null>(null);

  activeImageIdSignal = computed(() => {
    const images = this.images();
    if (this._activeImageId() === null && images?.length) {
      return images[0]?.id;
    }
    return this._activeImageId();
  });

  activeImage: Signal<Maybe<Image> | undefined> = computed(() => {
    const product = this.product();
    if (!product) {
      return undefined;
    }
    return this.images()?.find(image => image.id === this.activeImageIdSignal());
  });

  get activeImageId(): string | null {
    return this.activeImageIdSignal()
  }

  set activeImageId(value: string) {
    this._activeImageId.set(value);
  }

  onOrbitvuError() {
    this.orbitvuError.set(true);
    captureException('Orbitvu error', {
      extra: {
        productId: this.product()?.id,
        images: this.images(),
      }
    })
  }

  openImageProductModal() {
    this.trackEventService.trackClickEvent('product_image_modal_open', {
      image_id: this.activeImageId,
    })
    import('../../../../../../layout/components/modals/product-image-modal/product-image-modal.component').then(comp => {
      this.modalReference = this.modalService.open(comp.ProductImageModalComponent, {
        modalDialogClass: 'modal-dialog-bottom modal-large'
      });
      this.modalReference.componentInstance.product = this.product();
      this.modalReference.componentInstance.image = this.activeImage();
    })
  }
}
